import { MarkerType } from '@xyflow/react';

// Function to get current lead type
export const getCurrentLeadType = (leadType) => {
    const currentType = leadType || localStorage.getItem('current_leadType') || 'standard';
  
    return currentType;
};

// Function to save edge to localStorage
export const saveEdgeToLocalStorage = (edge, currentLeadType) => {
    try {
        
        
        const edgeKey = `edge-${currentLeadType}-${edge.source}-${edge.target}`;
      
        
        const edgeForStorage = {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            type: edge.type || 'default',
            animated: edge.animated || true,
            style: {
                stroke: '#2563eb',
                strokeWidth: 2
            },
            markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: '#2563eb'
            },
            data: edge.data || {}
        };
      
        
        const serializedEdge = JSON.stringify(edgeForStorage);
        
        
        localStorage.setItem(edgeKey, serializedEdge);
        
        // Verify the save
        const savedData = localStorage.getItem(edgeKey);
        
      
    } catch (error) {
        console.error("Error saving edge to localStorage:", error);
    }
};

// Function to load template from localStorage
export const loadTemplateFromLocalStorage = (nodeId, currentLeadType) => {
    try {
        const storedTemplate = localStorage.getItem(`template-${currentLeadType}-${nodeId}`);
        if (storedTemplate) {
            const parsedTemplate = JSON.parse(storedTemplate);
            
            // Handle headerMedia reconstruction if we have file info
            let headerMedia = parsedTemplate.headerMedia || null;
            if (headerMedia && headerMedia.fileInfo && headerMedia.base64) {
                try {
                    const mimeMatch = headerMedia.base64.match(/^data:([^;]+);base64,/);
                    const mimeType = mimeMatch?.[1] || headerMedia.fileInfo.type || 'image/jpeg';
                    const base64Data = headerMedia.base64.replace(/^data:([^;]+);base64,/, '');
                    const byteCharacters = atob(base64Data);
                    const byteArrays = [];
                    for (let i = 0; i < byteCharacters.length; i += 512) {
                        const slice = byteCharacters.slice(i, i + 512);
                        const byteNumbers = new Array(slice.length);
                        for (let j = 0; j < slice.length; j++) {
                            byteNumbers[j] = slice.charCodeAt(j);
                        }
                        byteArrays.push(new Uint8Array(byteNumbers));
                    }
                    const blob = new Blob(byteArrays, { type: mimeType });
                    const file = new File([blob], headerMedia.fileInfo.name, { 
                        type: mimeType,
                        lastModified: headerMedia.fileInfo.lastModified || Date.now()
                    });
                    
                    // Reconstruct the headerMedia object with both base64 and file
                    headerMedia = {
                        base64: headerMedia.base64,
                        file: file
                    };
                } catch (error) {
                    console.error("Error reconstructing file from localStorage:", error);
                    // Keep the base64 data even if file reconstruction fails
                    headerMedia = {
                        base64: headerMedia.base64
                    };
                }
            }
            
            return {
                templateName: parsedTemplate.templateName || "",
                headerType: parsedTemplate.headerType || "text",
                headerText: parsedTemplate.headerText || "",
                headerMedia: headerMedia,
                body: parsedTemplate.body || "",
                buttons: parsedTemplate.buttons || [],
                leadType: parsedTemplate.leadType || [],
                dummyValues: parsedTemplate.dummyValues || {},
                variable: parsedTemplate.variable || "",
                conditions: parsedTemplate.conditions || [],
                conditionString: parsedTemplate.conditionString || ""
            };
        }
        return null;
    } catch (error) {
        console.error("Error loading template from local storage:", error);
        return null;
    }
};

// Function to load all templates from localStorage
export const loadAllTemplatesFromLocalStorage = (currentLeadType) => {
    const templates = [];
    const templateCache = new Map();
  
    // Use a more efficient approach by checking specific keys first
    const templateKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`template-${currentLeadType}-`)) {
            templateKeys.push(key);
        }
    }
    
    // Process only the relevant keys
    templateKeys.forEach(key => {
        try {
            const templateData = JSON.parse(localStorage.getItem(key));
            // Extract nodeId by removing the prefix: template-${currentLeadType}-
            const nodeId = key.replace(`template-${currentLeadType}-`, '');
            
            // Check if template matches current lead type
            const templateLeadType = templateData._metadata?.leadType || 'standard';
            const templateType = templateData._metadata?.templateType || 'STANDARD_TEMPLATE';
            
            // Only include templates that match the current lead type
            if (templateLeadType === currentLeadType) {
                templates.push({
                    nodeId,
                    templateData: {
                        ...templateData,
                        templateType // Ensure template type is included
                    }
                });
            }
        } catch (error) {
            console.error("Error loading template:", error);
        }
    });
    
    return templates;
};

// Function to save template to localStorage
export const saveTemplateToLocalStorage = (nodeId, templateData, currentLeadType) => {
    try {
        // Handle headerMedia properly - store file info separately from base64
        let headerMediaToSave = null;
        if (templateData.headerMedia) {
            if (templateData.headerMedia.file) {
                // Store file information that can be serialized
                headerMediaToSave = {
                    base64: templateData.headerMedia.base64,
                    fileInfo: {
                        name: templateData.headerMedia.file.name,
                        size: templateData.headerMedia.file.size,
                        type: templateData.headerMedia.file.type,
                        lastModified: templateData.headerMedia.file.lastModified
                    }
                };
            } else {
                // Just store the base64 data if no file object
                headerMediaToSave = {
                    base64: templateData.headerMedia.base64
                };
            }
        }
        
        const templateToSave = {
            templateName: templateData.templateName || "",
            headerType: templateData.headerType || "text",
            headerText: templateData.headerText || "",
            headerMedia: headerMediaToSave,
            body: templateData.body || "",
            buttons: templateData.buttons || [],
            leadType: templateData.leadType || [],
            dummyValues: templateData.dummyValues || {},
            variable: templateData.variable || "",
            conditions: templateData.conditions || [],
            conditionString: templateData.conditionString || "",
            templateType: templateData.templateType || 'STANDARD_TEMPLATE',
            _metadata: {
                leadType: currentLeadType,
                templateType: templateData.templateType || 'STANDARD_TEMPLATE'
            }
        };
        
        const storageKey = `template-${currentLeadType}-${nodeId}`;
        localStorage.setItem(storageKey, JSON.stringify(templateToSave));
       
    } catch (error) {
        console.error("Error saving template to local storage:", error);
    }
};

// Function to save node position to localStorage
export const saveNodePositionToLocalStorage = (nodeId, position, templateName, currentLeadType) => {
    try {
        const nodeData = {
            position,
            templateName: templateName || ''
        };
        localStorage.setItem(`node-position-${currentLeadType}-${nodeId}`, JSON.stringify(nodeData));
    } catch (error) {
        console.error("Error saving node position to local storage:", error);
    }
};

// Function to load node position from localStorage
export const loadNodePositionFromLocalStorage = (nodeId, currentLeadType) => {
    try {
        const storedData = localStorage.getItem(`node-position-${currentLeadType}-${nodeId}`);
        if (storedData) {
            const parsedData = JSON.parse(storedData);
            if (parsedData.position) {
                return parsedData.position;
            } else {
                return parsedData;
            }
        }
        return null;
    } catch (error) {
        console.error("Error loading node position from local storage:", error);
        return null;
    }
};

// Function to load node template name from storage
export const loadNodeTemplateNameFromStorage = (nodeId, currentLeadType) => {
    try {
        const storedData = localStorage.getItem(`node-position-${currentLeadType}-${nodeId}`);
        if (storedData) {
            const parsedData = JSON.parse(storedData);
            if (parsedData.templateName) {
                return parsedData.templateName;
            }
        }
        return '';
    } catch (error) {
        console.error("Error loading node template name from storage:", error);
        return '';
    }
};

// Function to load all edges from localStorage
export const loadEdgesFromLocalStorage = (currentLeadType) => {

    
    const edges = [];
    const allKeys = Object.keys(localStorage);
    
    
    const edgeKeys = allKeys.filter(key => key.startsWith(`edge-${currentLeadType}-`));
   
    for (const key of edgeKeys) {
        
        try {
            const storedValue = localStorage.getItem(key);
           
            
            if (!storedValue) {
                console.warn(`6. Empty value for edge key: ${key}`);
                continue;
            }

            const edgeData = JSON.parse(storedValue);
            
            
            if (edgeData && edgeData.source && edgeData.target) {
                const edge = {
                    ...edgeData,
                    type: edgeData.type || 'default',
                    animated: edgeData.animated !== undefined ? edgeData.animated : true,
                    style: edgeData.style || {
                        stroke: '#2563eb',
                        strokeWidth: 2
                    },
                    markerEnd: edgeData.markerEnd || {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: '#2563eb'
                    },
                    data: edgeData.data || {}
                };
               
                edges.push(edge);
            } else {
                console.warn('9. Invalid edge data structure:', edgeData);
            }
        } catch (error) {
            console.error(`10. Error loading edge from localStorage key ${key}:`, error);
        }
    }
    
   
    return edges;
};

// Function to get template type
export const getTemplateType = (nodeType, templateData, currentLeadType) => {
    const templateTypeMap = {
        'birthday_template': 'BIRTHDAY_TEMPLATE',
        'birthday_offer_template': 'BIRTHDAY_OFFER_TEMPLATE',
        'festival_template': 'FESTIVAL_TEMPLATE',
        'festival_offer_template': 'FESTIVAL_OFFER_TEMPLATE',
        'conditional': 'CAMPAIGN_CONDITIONAL_TEMPLATE',
        'post_visit': 'POST_VISIT_TEMPLATE',
        'buildvizer': 'BUILDVIZER_TEMPLATE',
        'standard': 'STANDARD_TEMPLATE',
        'reply': 'REPLY_NODE_TEMPLATE'
    };

    if (nodeType && nodeType.startsWith('reply-')) {
        return 'REPLY_NODE_TEMPLATE';
    }

    if (templateData && templateData.buttons) {
        const hasSiteVisitButton = templateData.buttons.some(button => button.typeOfAction === 'site_visit');
        if (hasSiteVisitButton) {
            return 'SITE_VISIT_TEMPLATE';
        }
    }

    if (templateData && templateData.conditionString) {
        return 'CAMPAIGN_CONDITIONAL_TEMPLATE';
    }

    if (currentLeadType && templateTypeMap[currentLeadType.toLowerCase()]) {
        return templateTypeMap[currentLeadType.toLowerCase()];
    }

    return 'STANDARD_TEMPLATE';
};

// Function to check if scheduling is required
export const requiresScheduling = (currentLeadType) => {
    return currentLeadType === 'standard' || currentLeadType === 'birthday';
};

// Function to clear all campaign data from localStorage for a specific lead type
export const clearCampaignData = (currentLeadType) => {
    try {
        const keysToRemove = [];
        
        // Find all relevant keys
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (
                key.startsWith(`template-${currentLeadType}-`) ||
                key.startsWith(`node-position-${currentLeadType}-`) ||
                key.startsWith(`edge-${currentLeadType}-`)
            )) {
                keysToRemove.push(key);
            }
        }
        
        // Remove all keys
        keysToRemove.forEach(key => {
            try {
                localStorage.removeItem(key);
                console.log(`Cleared localStorage key: ${key}`);
            } catch (error) {
                console.error(`Error removing ${key} from localStorage:`, error);
            }
        });
        
        console.log(`Cleared ${keysToRemove.length} localStorage items for campaign type: ${currentLeadType}`);
        return keysToRemove.length;
    } catch (error) {
        console.error("Error clearing campaign data:", error);
        return 0;
    }
};

// Function to check if there are unsaved changes for a campaign
export const hasUnsavedChanges = (currentLeadType) => {
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (
                key.startsWith(`template-${currentLeadType}-`) ||
                key.startsWith(`edge-${currentLeadType}-`)
            )) {
                return true;
            }
        }
        return false;
    } catch (error) {
        console.error("Error checking for unsaved changes:", error);
        return false;
    }
};

// Function to save campaign state for auto-save functionality
export const saveCampaignState = (currentLeadType, campaignData) => {
    try {
        const autoSaveKey = `campaign-autosave-${currentLeadType}`;
        const autoSaveData = {
            timestamp: Date.now(),
            data: campaignData
        };
        localStorage.setItem(autoSaveKey, JSON.stringify(autoSaveData));
        console.log(`Auto-saved campaign state for ${currentLeadType}`);
    } catch (error) {
        console.error("Error auto-saving campaign state:", error);
    }
};

// Function to load auto-saved campaign state
export const loadAutoSavedCampaignState = (currentLeadType) => {
    try {
        const autoSaveKey = `campaign-autosave-${currentLeadType}`;
        const autoSaveData = localStorage.getItem(autoSaveKey);
        
        if (autoSaveData) {
            const parsed = JSON.parse(autoSaveData);
            const isRecent = Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000; // 24 hours
            
            if (isRecent) {
                console.log(`Found recent auto-saved campaign state for ${currentLeadType}`);
                return parsed.data;
            } else {
                // Clear old auto-save data
                localStorage.removeItem(autoSaveKey);
                console.log(`Cleared old auto-save data for ${currentLeadType}`);
            }
        }
        return null;
    } catch (error) {
        console.error("Error loading auto-saved campaign state:", error);
        return null;
    }
};

// Function to clear auto-saved campaign state
export const clearAutoSavedCampaignState = (currentLeadType) => {
    try {
        const autoSaveKey = `campaign-autosave-${currentLeadType}`;
        localStorage.removeItem(autoSaveKey);
        console.log(`Cleared auto-saved campaign state for ${currentLeadType}`);
    } catch (error) {
        console.error("Error clearing auto-saved campaign state:", error);
    }
};

// Function to get all campaign-related localStorage keys for a lead type
export const getCampaignStorageKeys = (currentLeadType) => {
    const keys = [];
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (
                key.startsWith(`template-${currentLeadType}-`) ||
                key.startsWith(`node-position-${currentLeadType}-`) ||
                key.startsWith(`edge-${currentLeadType}-`) ||
                key.startsWith(`campaign-autosave-${currentLeadType}`)
            )) {
                keys.push(key);
            }
        }
    } catch (error) {
        console.error("Error getting campaign storage keys:", error);
    }
    return keys;
};

// Function to delete a specific template from localStorage
export const deleteTemplateFromLocalStorage = (nodeId, currentLeadType) => {
    try {
        const templateKey = `template-${currentLeadType}-${nodeId}`;
        const positionKey = `node-position-${currentLeadType}-${nodeId}`;
        
        // Remove template data
        if (localStorage.getItem(templateKey)) {
            localStorage.removeItem(templateKey);
            console.log(`Deleted template data for node: ${nodeId}`);
        }
        
        // Remove position data
        if (localStorage.getItem(positionKey)) {
            localStorage.removeItem(positionKey);
            console.log(`Deleted position data for node: ${nodeId}`);
        }
        
        // Remove any edges connected to this node
        const edgeKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(`edge-${currentLeadType}-`)) {
                try {
                    const edgeData = JSON.parse(localStorage.getItem(key));
                    if (edgeData && (edgeData.source === nodeId || edgeData.target === nodeId)) {
                        edgeKeys.push(key);
                    }
                } catch (error) {
                    console.error(`Error parsing edge data for key ${key}:`, error);
                }
            }
        }
        
        // Remove connected edges
        edgeKeys.forEach(key => {
            localStorage.removeItem(key);
            console.log(`Deleted edge: ${key}`);
        });
        
        return true;
    } catch (error) {
        console.error("Error deleting template from localStorage:", error);
        return false;
    }
}; 